import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

// Upload a result-list PDF, let the backend (Claude vision) extract THIS user's
// participations, review them, then create competitionResult objects via the same
// store.insert() path the newCompetitionResult drawer uses.
@observer
class AiUploadCompetitionResult extends Component {
    constructor(props) {
        super(props);
        this.state = {
            analyzing: false,
            creating: false,
            error: '',
            competition: null,
            participations: [],
            selected: {},
        };
    }

    // The API envelope may nest the data; find the object that holds participations.
    resolvePayload = (obj) => {
        if (!obj || typeof obj !== 'object') {
            return null;
        }
        if (Array.isArray(obj.participations) || obj.competition) {
            return obj;
        }
        if (obj.data) {
            return this.resolvePayload(obj.data);
        }
        return null;
    }

    handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
            return;
        }
        await this.analyze(file);
        // Allow re-selecting the same file later.
        e.target.value = '';
    }

    analyze = async (file) => {
        this.setState({ analyzing: true, error: '', competition: null, participations: [], selected: {} });
        try {
            const formData = new FormData();
            formData.append('files[]', file, file.name);
            const response = await fetch(`${util.getApiServer()}/api/competitionresults/ai-extract`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${util.getJwtToken()}` },
                body: formData,
            });
            const body = await response.json();
            const payload = this.resolvePayload(body);
            if (!response.ok || !payload) {
                this.setState({ analyzing: false, error: 'parse' });
                return;
            }
            const participations = Array.isArray(payload.participations) ? payload.participations : [];
            const selected = {};
            participations.forEach((p, i) => { selected[i] = true; });
            this.setState({
                analyzing: false,
                competition: payload.competition || {},
                participations,
                selected,
            });
        } catch (err) {
            this.setState({ analyzing: false, error: 'network' });
        }
    }

    toggle = (idx) => {
        this.setState(prev => ({ selected: { ...prev.selected, [idx]: !prev.selected[idx] } }));
    }

    reset = () => {
        this.setState({ competition: null, participations: [], selected: {}, error: '' });
    }

    create = async () => {
        const { callback = () => {} } = this.props;
        const { competitionResultStore, appState } = this.props.stores;
        const { competition, participations, selected } = this.state;
        this.setState({ creating: true });

        for (let idx = 0; idx < participations.length; idx += 1) {
            if (!selected[idx]) {
                continue;
            }
            const p = participations[idx];
            const obj = {
                title: competition.title,
                competitionId: competition.competitionId,
                organizer: competition.organizer,
                place: competition.place,
                date: competition.date,
                program: p.program,
                class: p.class,
            };
            if (p.rank !== null && p.rank !== undefined) {
                obj.rank = p.rank;
            }
            if (p.score !== null && p.score !== undefined) {
                obj.score = p.score;
            }
            if (p.promotionClass) {
                obj.promotionClass = p.promotionClass;
            }
            // eslint-disable-next-line no-await-in-loop
            await competitionResultStore.insert(obj);
        }

        this.setState({ creating: false });
        callback();
        appState.toggleDrawer();
    }

    render() {
        const {
            analyzing, creating, error, competition, participations, selected,
        } = this.state;
        const analyzed = competition !== null;
        const selectedCount = participations.filter((p, i) => selected[i]).length;

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 120px;'>
                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        <Text id='competitionResult.aiUpload-title'>Import results from result list</Text>
                    </div>

                    {!analyzed && !analyzing && <div class='px-3 mt-3'>
                        <p class='text-body-secondary'>
                            <Text id='competitionResult.aiUpload-help'>Upload a PDF result list. The AI finds your participations so you can review them before creating.</Text>
                        </p>
                        <label class='btn btn-primary rounded-pill'>
                            <i class='fa-duotone fa-file-pdf me-1' /> <Text id='competitionResult.aiUpload-choose'>Choose PDF file</Text>
                            <input type='file' accept='application/pdf,.pdf' class='d-none' onChange={this.handleFile} />
                        </label>
                        {error && <div class='alert alert-danger mt-3'>
                            <Text id='competitionResult.aiUpload-error'>Could not read the result list. Please try again.</Text>
                        </div>}
                    </div>}

                    {analyzing && <div class='text-center py-5'>
                        <div class='spinner-border text-primary' role='status' />
                        <p class='mt-3 text-body-secondary'>
                            <Text id='competitionResult.aiUpload-analyzing'>Analyzing result list …</Text>
                        </p>
                    </div>}

                    {analyzed && !analyzing && <div class='px-3 mt-3'>
                        <div class='card mb-3'>
                            <div class='card-body py-2'>
                                <h5 class='card-title mb-1'>{competition.title || <Text id='competitionResult.aiUpload-competition'>Competition</Text>}</h5>
                                <div class='text-body-secondary small'>
                                    {competition.date && <span>{competition.date}</span>}
                                    {competition.competitionId && <span> · #{competition.competitionId}</span>}
                                    {competition.organizer && <span> · {competition.organizer}</span>}
                                    {competition.place && competition.place !== competition.organizer && <span> · {competition.place}</span>}
                                </div>
                            </div>
                        </div>

                        {participations.length === 0 ? <div class='alert alert-warning'>
                            <Text id='competitionResult.aiUpload-none'>Found no participations for you in this list.</Text>
                        </div> : <>
                            <div class='d-flex justify-content-between align-items-center mb-2'>
                                <span class='text-body-secondary small'>
                                    <Text id='competitionResult.aiUpload-found' fields={{ count: participations.length }}>
                                        Found {participations.length} participations
                                    </Text>
                                </span>
                                <button type='button' class='btn btn-sm btn-link' onClick={this.reset}>
                                    <Text id='competitionResult.aiUpload-another'>Choose another file</Text>
                                </button>
                            </div>
                            <div class='list-group'>
                                {participations.map((p, idx) => (
                                    <label class='list-group-item d-flex align-items-center'>
                                        <input
                                            type='checkbox'
                                            class='form-check-input me-3 flex-shrink-0'
                                            checked={!!selected[idx]}
                                            onChange={() => this.toggle(idx)}
                                        />
                                        <span class='flex-grow-1'>
                                            <span class='badge text-bg-primary me-2'>{p.program}</span>
                                            {p.class && <span class='me-2'>{p.class}</span>}
                                            {p.club && <small class='text-body-secondary'>{p.club}</small>}
                                        </span>
                                        <span class='text-end small text-body-secondary'>
                                            <Text id='competitionResult.aiUpload-place'>Place</Text> {p.rank !== null && p.rank !== undefined ? p.rank : '-'}
                                            {' · '}
                                            <Text id='competitionResult.aiUpload-score'>Sum</Text> {p.score !== null && p.score !== undefined ? p.score : '-'}
                                            {p.promotionClass ? <span class='ms-1 badge bg-success'>→ {p.promotionClass}</span> : ''}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </>}
                    </div>}
                </div>
            </div>

            {analyzed && !analyzing && participations.length > 0 && <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style='bottom: 0; left: 0;'>
                <button type='button' class='btn btn-primary rounded-pill' disabled={creating || selectedCount === 0} onClick={this.create}>
                    {creating
                        ? <><span class='spinner-border spinner-border-sm me-2' /><Text id='competitionResult.aiUpload-creating'>Creating …</Text></>
                        : <Text id='competitionResult.aiUpload-create' fields={{ count: selectedCount }}>Create {selectedCount} selected</Text>}
                </button>
            </div>}
        </>);
    }
}

export default AiUploadCompetitionResult;
